/**
 * AtcManifestMemoryCache - In-memory (Tier 1) cache for ATC manifests
 *
 * Single Responsibility: Manages volatile, session-scoped caches for
 * main and station manifests with configurable TTL.
 *
 * This is the fastest tier in the three-tier caching strategy:
 * 1. In-memory cache (this service) — fastest, session-only
 * 2. localStorage cache (AtcManifestCacheService) — persistent across sessions
 * 3. Network fetch — fallback when caches miss or are stale
 */

import { AtcMainManifest, AtcStationManifest } from '@/types/AtcManifest';

export interface CachedEntry<T> {
  data: T;
  timestamp: number;
}

export class AtcManifestMemoryCache {
  private static readonly MEMORY_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  private static mainManifestCache: CachedEntry<AtcMainManifest> | null = null;
  private static stationManifestCache = new Map<string, CachedEntry<AtcStationManifest>>();

  // ── Main manifest ──────────────────────────────────────────────

  /**
   * Get cached main manifest if present and not expired.
   * @returns The cached entry or null if missing/expired.
   */
  static getMainManifest(): CachedEntry<AtcMainManifest> | null {
    if (!this.mainManifestCache) return null;
    const age = Date.now() - this.mainManifestCache.timestamp;
    if (age >= this.MEMORY_CACHE_TTL) return null;
    return this.mainManifestCache;
  }

  /**
   * Set (or overwrite) the in-memory main manifest cache.
   */
  static setMainManifest(data: AtcMainManifest, timestamp = Date.now()): void {
    this.mainManifestCache = { data, timestamp };
  }

  /**
   * Unconditionally read the current entry (even if stale) — used when
   * promoting localStorage data into the memory tier.
   */
  static getMainManifestRaw(): CachedEntry<AtcMainManifest> | null {
    return this.mainManifestCache;
  }

  // ── Station manifest ───────────────────────────────────────────

  /**
   * Get cached station manifest if present and not expired.
   */
  static getStationManifest(cacheKey: string): CachedEntry<AtcStationManifest> | null {
    const cached = this.stationManifestCache.get(cacheKey);
    if (!cached) return null;
    const age = Date.now() - cached.timestamp;
    if (age >= this.MEMORY_CACHE_TTL) return null;
    return cached;
  }

  /**
   * Set (or overwrite) the in-memory station manifest cache.
   */
  static setStationManifest(cacheKey: string, data: AtcStationManifest, timestamp = Date.now()): void {
    this.stationManifestCache.set(cacheKey, { data, timestamp });
  }

  // ── Cache management ───────────────────────────────────────────

  /**
   * Clear all in-memory caches.
   */
  static clearAll(): void {
    this.mainManifestCache = null;
    this.stationManifestCache.clear();
  }

  /**
   * Clear cache for a single station.
   */
  static clearStation(cacheKey: string): void {
    this.stationManifestCache.delete(cacheKey);
  }
}
